// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/pages/SellerDashboardPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import type { LeadCaptureSession } from '@troia-v3/types';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { LeadCaptureModal } from '@/modules/crm/capture/components/LeadCaptureModal';
import { Button, PageToolbar, PeriodPicker } from '@/shared/components/ui';
import type { PeriodPickerSegment } from '@/shared/components/ui';

import { MyLeadsCard } from '../components/MyLeadsCard';
import { MyLevelCard } from '../components/MyLevelCard';
import { RecentPointsCard } from '../components/RecentPointsCard';
import { SellerCelebration } from '../components/SellerCelebration';
import { SellerHeroCta } from '../components/SellerHeroCta';
import { SellerKpiRow } from '../components/SellerKpiRow';
import { SellerMobileHome, type SellerMobileTab } from '../components/SellerMobileHome';
import { StoreRankingCard } from '../components/StoreRankingCard';
import { useSellerDashboard } from '../hooks/useSellerDashboard';
import {
  customRangeDays,
  formatPeriodLabel,
  formatPts,
  SELLER_CUSTOM_MAX_DAYS,
  type SellerPeriod,
  type SellerPeriodValue,
} from '../lib/sellerFormat';

import '../seller-dashboard.css';

/** Segmentos do `PeriodPicker` (+ o botão "Personalizado" que ele já traz). */
const PERIOD_SEGMENTS: ReadonlyArray<PeriodPickerSegment<SellerPeriod>> = [
  { key: 'today', label: 'Hoje' }, { key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' },
];
const MOBILE_TABS: Array<{ id: SellerMobileTab; label: string }> = [
  { id: 'home', label: 'Início' }, { id: 'leads', label: 'Meus leads' }, { id: 'ranking', label: 'Ranking' },
];

/**
 * Dashboard do vendedor — `/crm/seller` (mockups 07 desktop / 08 celular).
 * Dados: `GET /api/leads/seller-dashboard` (crm:read). Tempo real: o
 * `useLeadCaptureSocket` global (GlobalSocketListeners) invalida
 * `['seller-dashboard']` a cada mudança de sessão — nenhum `socket.on` aqui
 * (NUNCA #38). FAB da bottom nav (`troia:mobile-fab`) abre a modal de captura.
 */
export const SellerDashboardPage: React.FC = () => {
  const { state } = useAuth();
  const [period, setPeriod] = useState<SellerPeriodValue>({ mode: 'month' });
  const [mobileTab, setMobileTab] = useState<SellerMobileTab>('home');
  const [modalOpen, setModalOpen] = useState(false);
  const [resumeUuid, setResumeUuid] = useState<string | undefined>(undefined);
  const [celebration, setCelebration] = useState<number | null>(null);

  const { data, isLoading, error } = useSellerDashboard(period);

  /**
   * Chips "Hoje/Semana/Mês" do card "Meus leads" e do celular: voltam para o
   * período pronto. `useCallback` sem dependências de propósito — o
   * `SellerMobileHome` usa a identidade disto num efeito (ver lá).
   */
  const selectPreset = useCallback((p: SellerPeriod) => setPeriod({ mode: p }), []);

  /**
   * Seletor do toolbar. O `PeriodPicker` só confere início <= fim; o teto de
   * dias é do backend (422 acima de `SELLER_CUSTOM_MAX_DAYS`), então barra aqui
   * antes — senão o dashboard inteiro trocaria pela caixa de erro.
   */
  const handlePeriodChange = useCallback((value: SellerPeriodValue) => {
    if (value.mode === 'custom' && value.start && value.end && customRangeDays(value.start, value.end) > SELLER_CUSTOM_MAX_DAYS) {
      toast.error(`O período personalizado pode ter no máximo ${SELLER_CUSTOM_MAX_DAYS} dias.`);
      return;
    }
    setPeriod(value);
  }, []);

  const openCapture = useCallback((session?: LeadCaptureSession) => {
    setResumeUuid(session?.uuid);
    setModalOpen(true);
  }, []);

  /**
   * G4: `pointsEarned` vem da SESSÃO (gravado pelo backend a partir do
   * livro-caixa). Com gamificação desligada, regra off ou cap diário batido
   * ele é 0 — e aí NÃO se comemora: "Jornada concluída! +0 pontos" era pior
   * que silêncio.
   */
  const handleCompleted = useCallback((pointsEarned: number) => {
    if (pointsEarned > 0) setCelebration(pointsEarned);
  }, []);
  const closeCelebration = useCallback(() => setCelebration(null), []);

  // "+" central da bottom nav → abre a modal (preventDefault = tratado, sem fallback pro sheet "Mais").
  useEffect(() => {
    const onFab = (e: Event): void => { e.preventDefault(); openCapture(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [openCapture]);

  const pending = data?.pendingSessions ?? [];
  // `SellerDashboardResponse.unit` (Parte 1 Task 1) — preenchido pelo service quando o ranking é `users_by_unit`.
  const unitName = data?.unit?.name;
  const periodChip = data ? formatPeriodLabel(period.mode, data.periodKey) : '';
  const userId = state.user?.id ?? '';

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="seller-dashboard-page">
      <div className="flex-none sticky top-0 z-20">
        <PageToolbar
          data-testid="seller-toolbar"
          mobileBreakpoint={900}
          left={
            <>
              <span className="text-base font-semibold tracking-[-.01em] whitespace-nowrap" style={{ color: 'var(--theme-header-text, var(--theme-card-text))' }}>Minhas Vendas</span>
              {periodChip && (
                <span className="hidden sm:inline-flex items-center h-[26px] px-[11px] rounded-full text-xs font-semibold flex-shrink-0"
                  style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)', fontVariantNumeric: 'tabular-nums' }} data-testid="seller-period-chip">{periodChip}</span>
              )}
            </>
          }
          right={
            <div className="flex items-center gap-2">
              {/* testids: seller-period-{today|week|month|custom}, -popover, -custom-start/-end/-apply */}
              <PeriodPicker<SellerPeriod> value={period} onChange={handlePeriodChange} segments={PERIOD_SEGMENTS} data-testid="seller-period" />
              <span className="w-px h-6 mx-1" style={{ backgroundColor: 'var(--theme-header-border)' }} />
              <Button variant="primary" size="sm" icon="qr-code" onClick={() => openCapture()} collapsibleLabel data-testid="seller-toolbar-start">Iniciar atendimento</Button>
            </div>
          }
          mobile={
            <div className="flex gap-0.5 px-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }} role="tablist">
              {MOBILE_TABS.map((t) => (
                <button key={t.id} type="button" role="tab" aria-selected={mobileTab === t.id} onClick={() => setMobileTab(t.id)}
                  className="h-11 px-3 text-[13px] font-semibold -mb-px border-b-2"
                  style={{ color: mobileTab === t.id ? 'var(--theme-primary)' : 'var(--theme-muted-text)', borderColor: mobileTab === t.id ? 'var(--theme-primary)' : 'transparent' }}
                  data-testid={`seller-mobile-tab-${t.id}`}>{t.label}</button>
              ))}
            </div>
          }
        />
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-card-border)', color: 'var(--theme-error-fg)' }} data-testid="seller-dashboard-error">
              Não foi possível carregar o seu dashboard: {(error as Error).message}
            </div>
          )}
          {isLoading && (
            <div className="rounded-xl p-8 text-center" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }} data-testid="seller-dashboard-loading">
              <p style={{ color: 'var(--theme-muted-text)' }}>Carregando…</p>
            </div>
          )}

          {data && !isLoading && (
            <>
              {/* Desktop (mockup 07) */}
              <div className="hidden min-[900px]:flex flex-col gap-[18px]">
                <SellerHeroCta pending={pending} onStart={() => openCapture()} onResume={openCapture} />
                <SellerKpiRow kpis={data.kpis} ranking={data.ranking} periodKey={data.periodKey} gamificationPeriod={data.gamificationPeriod} {...(unitName ? { unitName } : {})} />
                <div className="grid grid-cols-[3fr_2fr] gap-3.5 items-start">
                  <div className="flex flex-col gap-2.5 min-w-0">
                    <MyLeadsCard leads={data.recentLeads} period={period.mode} onPeriodChange={selectPreset} pending={pending} />
                  </div>
                  <div className="flex flex-col gap-2.5 min-w-0">
                    <StoreRankingCard ranking={data.ranking} userId={userId} {...(unitName ? { unitName } : {})} />
                    <MyLevelCard totalPoints={data.kpis.totalPoints} level={data.kpis.level} {...(data.kpis.nextLevel ? { nextLevel: data.kpis.nextLevel } : {})} />
                    <RecentPointsCard events={data.recentEvents} />
                  </div>
                </div>
              </div>

              {/* Celular (mockup 08) — anatomia própria */}
              <div className="min-[900px]:hidden pb-4">
                <SellerMobileHome tab={mobileTab} data={data} firstName={state.user?.firstName ?? 'vendedor'} userId={userId}
                  onPeriodChange={selectPreset} onStart={() => openCapture()} onTabChange={setMobileTab} {...(unitName ? { unitName } : {})} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky "Você" da aba Ranking (mockup 08, .m-sticky) — acima da bottom nav */}
      {data?.ranking?.me && mobileTab === 'ranking' && data.ranking.visibility !== 'only_me' && (
        <div className="min-[900px]:hidden fixed left-3.5 right-3.5 z-40 rounded-xl px-3 py-2.5 flex items-center gap-2.5"
          style={{
            bottom: 'calc(84px + env(safe-area-inset-bottom))',
            // Tokens do tema: a barra flutua SOBRE a página e ficava branca com
            // texto quase preto no modo escuro (SEMPRE #4). Não é modal/drawer,
            // então não vale a exceção do tema claro fixo.
            background: 'var(--theme-overlay-card-background)',
            border: '1px solid var(--theme-card-border)',
            boxShadow: 'var(--shadow-lg)',
          }} data-testid="seller-mobile-sticky-me">
          <span className={`seller-rk-pos ${data.ranking.me.position <= 3 ? ['seller-rk-pos--gold', 'seller-rk-pos--silver', 'seller-rk-pos--bronze'][data.ranking.me.position - 1] : ''}`}>{data.ranking.me.position}</span>
          <div className="flex-1">
            <div className="font-bold text-[13px]" style={{ color: 'var(--theme-card-text)' }}>Você · {formatPts(data.ranking.me.points)} pts</div>
            {data.ranking.me.gapToNext !== undefined && data.ranking.me.position > 1 && (
              <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>faltam {formatPts(data.ranking.me.gapToNext)} pts para o #{data.ranking.me.position - 1}</div>
            )}
          </div>
          {data.ranking.me.trend !== 'flat' && (
            <span className="text-xs font-semibold" style={{ color: data.ranking.me.trend === 'up' ? 'var(--theme-success-fg)' : 'var(--theme-error-fg)' }}>{data.ranking.me.trend === 'up' ? '↑ 1' : '↓ 1'}</span>
          )}
        </div>
      )}

      <LeadCaptureModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setResumeUuid(undefined); }}
        {...(resumeUuid ? { resumeSessionUuid: resumeUuid } : {})}
        {...(data?.gamificationPeriod ? { gamificationPeriod: data.gamificationPeriod } : {})}
        onCompleted={handleCompleted}
      />
      {celebration !== null && <SellerCelebration points={celebration} onDone={closeCelebration} />}
    </div>
  );
};
SellerDashboardPage.displayName = 'SellerDashboardPage';
